/**
 * DataMoonSourcePanel.tsx
 *
 * Tenant-user panel for sourcing prospects from DataMoon.
 * Drop this into IcpProfilesPage.tsx as a collapsible section on each ICP card,
 * or mount it as a standalone dialog triggered by a "Source from DataMoon" button.
 *
 * Flow:
 *   1. On mount, GET /api/v1/datamoon/status — if not enabled, show a "not available" note.
 *   2. User sees required ICP-mapped filters (industry, company size, seniority,
 *      geography) pre-populated + business_email has_value (mandatory, non-removable).
 *   3. User can add additional filters via "+ Add filter".
 *   4. User clicks "Fetch Prospects" → POST /api/v1/datamoon/fetch-prospects.
 *   5. Result summary shown: imported / skipped duplicates / skipped no-name.
 *
 * Usage:
 *   <DataMoonSourcePanel icp={selectedIcp} onImported={() => qc.invalidateQueries(...)} />
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Database,
  Plus,
  Trash2,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Info,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import { datamoonApi } from "@/services/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { NativeSelect } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";

// ── Types ──────────────────────────────────────────────────────────────────

interface IcpProfile {
  id: string;
  name: string;
  persona: string | null;
  companyType: string | null;
  painPoints: string[];
  buyingSignals?: string[];
}

interface FilterRow {
  id: string; // local UI key
  field: string;
  operator: string;
  value: string; // serialised; "null" means has_value with no value
}

interface ImportResult {
  audience_id: number;
  rows_fetched: number;
  imported: number;
  skipped_duplicate: number;
  skipped_no_name: number;
  csv_filename?: string | null;
}

// ── Constants ──────────────────────────────────────────────────────────────

// US states + territories for the State / Region multi-select
const US_STATE_OPTIONS = [
  { value: "Alabama", label: "Alabama" },
  { value: "Alaska", label: "Alaska" },
  { value: "Arizona", label: "Arizona" },
  { value: "Arkansas", label: "Arkansas" },
  { value: "California", label: "California" },
  { value: "Colorado", label: "Colorado" },
  { value: "Connecticut", label: "Connecticut" },
  { value: "Delaware", label: "Delaware" },
  { value: "Florida", label: "Florida" },
  { value: "Georgia", label: "Georgia" },
  { value: "Hawaii", label: "Hawaii" },
  { value: "Idaho", label: "Idaho" },
  { value: "Illinois", label: "Illinois" },
  { value: "Indiana", label: "Indiana" },
  { value: "Iowa", label: "Iowa" },
  { value: "Kansas", label: "Kansas" },
  { value: "Kentucky", label: "Kentucky" },
  { value: "Louisiana", label: "Louisiana" },
  { value: "Maine", label: "Maine" },
  { value: "Maryland", label: "Maryland" },
  { value: "Massachusetts", label: "Massachusetts" },
  { value: "Michigan", label: "Michigan" },
  { value: "Minnesota", label: "Minnesota" },
  { value: "Mississippi", label: "Mississippi" },
  { value: "Missouri", label: "Missouri" },
  { value: "Montana", label: "Montana" },
  { value: "Nebraska", label: "Nebraska" },
  { value: "Nevada", label: "Nevada" },
  { value: "New Hampshire", label: "New Hampshire" },
  { value: "New Jersey", label: "New Jersey" },
  { value: "New Mexico", label: "New Mexico" },
  { value: "New York", label: "New York" },
  { value: "North Carolina", label: "North Carolina" },
  { value: "North Dakota", label: "North Dakota" },
  { value: "Ohio", label: "Ohio" },
  { value: "Oklahoma", label: "Oklahoma" },
  { value: "Oregon", label: "Oregon" },
  { value: "Pennsylvania", label: "Pennsylvania" },
  { value: "Rhode Island", label: "Rhode Island" },
  { value: "South Carolina", label: "South Carolina" },
  { value: "South Dakota", label: "South Dakota" },
  { value: "Tennessee", label: "Tennessee" },
  { value: "Texas", label: "Texas" },
  { value: "Utah", label: "Utah" },
  { value: "Vermont", label: "Vermont" },
  { value: "Virginia", label: "Virginia" },
  { value: "Washington", label: "Washington" },
  { value: "West Virginia", label: "West Virginia" },
  { value: "Wisconsin", label: "Wisconsin" },
  { value: "Wyoming", label: "Wyoming" },
  { value: "District of Columbia", label: "District of Columbia" },
  { value: "Puerto Rico", label: "Puerto Rico" },
  { value: "Guam", label: "Guam" },
];

// Pre-defined options for required ICP-mapped filters

const INDUSTRY_OPTIONS = [
  "",
  "Accounting",
  "Advertising",
  "Aerospace & Defense",
  "Agriculture",
  "Apparel & Fashion",
  "Architecture & Planning",
  "Automotive",
  "Aviation",
  "Banking",
  "Biotechnology",
  "Building Materials",
  "Business Services",
  "Chemicals",
  "Civil Engineering",
  "Computer & Network Security",
  "Computer Hardware",
  "Computer Software",
  "Construction",
  "Consumer Electronics",
  "Consumer Goods",
  "Consumer Services",
  "Cosmetics",
  "Defense & Space",
  "Design",
  "E-Learning",
  "Education Management",
  "Electrical/Electronic Manufacturing",
  "Energy",
  "Entertainment",
  "Environmental Services",
  "Events Services",
  "Executive Office",
  "Facilities Services",
  "Financial Services",
  "Food & Beverages",
  "Food Production",
  "Furniture",
  "Government Administration",
  "Government Relations",
  "Graphic Design",
  "Health, Wellness & Fitness",
  "Higher Education",
  "Hospital & Health Care",
  "Hospitality",
  "Human Resources",
  "Import & Export",
  "Industrial Automation",
  "Information Services",
  "Information Technology & Services",
  "Insurance",
  "International Trade & Development",
  "Internet",
  "Investment Banking",
  "Investment Management",
  "Law Practice",
  "Legal Services",
  "Leisure, Travel & Tourism",
  "Logistics & Supply Chain",
  "Luxury Goods & Jewelry",
  "Machinery",
  "Management Consulting",
  "Manufacturing",
  "Market Research",
  "Marketing & Advertising",
  "Mechanical/Industrial Engineering",
  "Media Production",
  "Medical Devices",
  "Medical Practice",
  "Mining & Metals",
  "Music",
  "Nanotechnology",
  "Non-Profit Organization Management",
  "Oil & Energy",
  "Online Media",
  "Outsourcing/Offshoring",
  "Packaging & Containers",
  "Paper & Forest Products",
  "Performing Arts",
  "Pharmaceuticals",
  "Philanthropy",
  "Photography",
  "Plastics",
  "Primary/Secondary Education",
  "Printing",
  "Professional Training & Coaching",
  "Public Relations & Communications",
  "Publishing",
  "Railroad Manufacturing",
  "Real Estate",
  "Recreational Facilities & Services",
  "Religious Institutions",
  "Renewables & Environment",
  "Research",
  "Restaurants",
  "Retail",
  "Security & Investigations",
  "Semiconductors",
  "Shipbuilding",
  "Sporting Goods",
  "Sports",
  "Staffing & Recruiting",
  "Supermarkets",
  "Telecommunications",
  "Textiles",
  "Think Tanks",
  "Tobacco",
  "Translation & Localization",
  "Transportation/Trucking/Railroad",
  "Utilities",
  "Venture Capital & Private Equity",
  "Veterinary",
  "Warehousing",
  "Wholesale",
  "Wine & Spirits",
  "Wireless",
  "Writing & Editing",
];

const COMPANY_SIZE_OPTIONS = [
  { value: "1 to 10", label: "1 – 10" },
  { value: "11 to 25", label: "11 – 25" },
  { value: "26 to 50", label: "26 – 50" },
  { value: "51 to 100", label: "51 – 100" },
  { value: "101 to 250", label: "101 – 250" },
  { value: "251 to 500", label: "251 – 500" },
  { value: "501 to 1000", label: "501 – 1,000" },
  { value: "1001 to 5000", label: "1,001 – 5,000" },
  { value: "5001 to 10000", label: "5,001 – 10,000" },
  { value: "10000+", label: "10,000+" },
];

const SENIORITY_OPTIONS = [
  { value: "cxo", label: "C-Level / CxO" },
  { value: "vice president", label: "VP" },
  { value: "director", label: "Director" },
  { value: "manager", label: "Manager" },
  { value: "senior individual contributor", label: "Senior IC" },
  { value: "partner", label: "Partner" },
  { value: "owner", label: "Owner" },
];

const REVENUE_OPTIONS = [
  { value: "under 1 million", label: "Under $1M" },
  { value: "1 million to 5 million", label: "$1M – $5M" },
  { value: "5 million to 10 million", label: "$5M – $10M" },
  { value: "10 million to 25 million", label: "$10M – $25M" },
  { value: "25 million to 50 million", label: "$25M – $50M" },
  { value: "50 million to 100 million", label: "$50M – $100M" },
  { value: "100 million to 250 million", label: "$100M – $250M" },
  { value: "250 million to 500 million", label: "$250M – $500M" },
  { value: "500 million to 1 billion", label: "$500M – $1B" },
  { value: "1 billion and over", label: "$1B+" },
];

// Additional filter fields for "+ Add filter"
const EXTRA_FIELD_OPTIONS = [
  { value: "job_title", label: "Job Title" },
  { value: "company_sic", label: "Company SIC Code" },
  { value: "company_domain", label: "Company Domain" },
  { value: "company_name", label: "Company Name" },
  { value: "personal_state", label: "Personal State" },
  { value: "professional_state", label: "Professional State" },
  { value: "department", label: "Department" },
  { value: "homeowner", label: "Homeowner" },
  { value: "income_range", label: "Income Range" },
  { value: "net_worth", label: "Net Worth" },
];

const EXTRA_OPERATOR_OPTIONS = [
  { value: "has_value", label: "Has value" },
  { value: "has_no_value", label: "Has no value" },
  { value: "=", label: "Equals" },
  { value: "!=", label: "Not equals" },
  { value: "in", label: "In (comma-separated)" },
  { value: "not_in", label: "Not in (comma-separated)" },
  { value: "contains", label: "Contains" },
  { value: "not_contains", label: "Not contains" },
];

const NO_VALUE_OPERATORS = new Set(["has_value", "has_no_value"]);

// ── Helpers ────────────────────────────────────────────────────────────────

/** Convert an extra filter row to the shape the API expects. */
function rowToApiFilter(row: FilterRow): Record<string, unknown> {
  if (NO_VALUE_OPERATORS.has(row.operator)) {
    return { field: row.field, operator: row.operator, value: null };
  }
  if (row.operator === "in" || row.operator === "not_in") {
    const arr = row.value
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    return { field: row.field, operator: row.operator, value: arr };
  }
  return { field: row.field, operator: row.operator, value: row.value };
}

// ── Checkbox group component ──────────────────────────────────────────────

function CheckboxGroup({
  options,
  selected,
  onChange,
}: {
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (selected: string[]) => void;
}) {
  const toggle = (val: string) => {
    if (selected.includes(val)) {
      onChange(selected.filter((s) => s !== val));
    } else {
      onChange([...selected, val]);
    }
  };
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <label
          key={o.value}
          className={`flex cursor-pointer items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs transition-colors ${
            selected.includes(o.value)
              ? "border-primary bg-primary/10 text-primary font-medium"
              : "border-border bg-background text-muted-foreground hover:border-primary/50"
          }`}
        >
          <input
            type="checkbox"
            className="sr-only"
            checked={selected.includes(o.value)}
            onChange={() => toggle(o.value)}
          />
          {o.label}
        </label>
      ))}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

interface DataMoonSourcePanelProps {
  icp?: IcpProfile | null;
  /** Default limit to pre-fill — taken from the ICP's saved datamoon_limit. */
  initialLimit?: string;
  onImported?: (result: ImportResult) => void;
}

export function DataMoonSourcePanel({ icp, initialLimit, onImported }: DataMoonSourcePanelProps) {
  // ── Status ──────────────────────────────────────────────────────────────
  const statusQ = useQuery({
    queryKey: ["datamoon-status"],
    queryFn: () => datamoonApi.status(),
    retry: false,
  });

  const status = statusQ.data;
  const isEnabled = status?.enabled ?? false;
  const quotaRemaining =
    status?.prospect_quota != null && status?.quota_used != null
      ? status.prospect_quota - status.quota_used
      : null;

  // ── Required filter state (ICP-mapped defaults) ────────────────────────
  const [industry, setIndustry] = useState("");
  const [customIndustry, setCustomIndustry] = useState("");
  const [states, setStates] = useState<string[]>([]);
  const [stateSearch, setStateSearch] = useState("");
  const [naicsCodes, setNaicsCodes] = useState("");
  const [seniorities, setSeniorities] = useState<string[]>([]);
  const [companySizes, setCompanySizes] = useState<string[]>([]);
  const [revenue, setRevenue] = useState<string[]>([]);

  // ── Extra filter state ─────────────────────────────────────────────────
  const [extraFilters, setExtraFilters] = useState<FilterRow[]>([]);
  const [showExtras, setShowExtras] = useState(false);

  // ── Other state ────────────────────────────────────────────────────────
  const [limit, setLimit] = useState(initialLimit ?? "500");
  const [lastResult, setLastResult] = useState<ImportResult | null>(null);

  // ── Pre-seed from ICP when it changes ──────────────────────────────────
  useEffect(() => {
    if (icp?.companyType) {
      // Try to match companyType to an industry option (case-insensitive)
      const match = INDUSTRY_OPTIONS.find(
        (o) => o.toLowerCase() === icp.companyType!.toLowerCase()
      );
      if (match) {
        setIndustry(match);
      } else {
        // Use as-is — user can adjust
        setIndustry(icp.companyType);
      }
    }
  }, [icp?.id]);

  // ── Build final filter payload ─────────────────────────────────────────
  function buildFilters(): Record<string, unknown>[] {
    const filters: Record<string, unknown>[] = [];

    // 1. Business Email — always required (non-removable)
    filters.push({ field: "business_email", operator: "has_value", value: null });

    // 2. Company Industry — DataMoon field: primary_industry, always "in" + array
    const effectiveIndustry = customIndustry.trim() || industry;
    if (effectiveIndustry) {
      filters.push({
        field: "primary_industry",
        operator: "in",
        value: [effectiveIndustry],
      });
    }

    // 3. State / Region (multiselect → always "in" with array)
    if (states.length > 0) {
      filters.push({ field: "company_state", operator: "in", value: states });
    }

    // 4. Company NAICS Code (comma-separated codes → "in")
    const naicsArr = naicsCodes
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (naicsArr.length > 0) {
      filters.push({
        field: "company_naics",
        operator: "in",
        value: naicsArr,
      });
    }

    // 5. Seniority Level (multiselect, lowercase values)
    if (seniorities.length > 0) {
      filters.push({
        field: "seniority_level_2",
        operator: "in",
        value: seniorities,
      });
    }

    // 6. Company Employee Count Range (multiselect string buckets)
    if (companySizes.length > 0) {
      filters.push({
        field: "company_employee_count",
        operator: "in",
        value: companySizes,
      });
    }

    // 7. Company Revenue Range (multiselect string buckets)
    if (revenue.length > 0) {
      filters.push({
        field: "company_revenue",
        operator: "in",
        value: revenue,
      });
    }

    // 8. Extra user-added filters
    for (const row of extraFilters) {
      if (row.field) {
        filters.push(rowToApiFilter(row));
      }
    }

    return filters;
  }

  // ── Fetch mutation ──────────────────────────────────────────────────────
  const fetchMut = useMutation({
    mutationFn: () => {
      return datamoonApi.fetchProspects({
        filters: buildFilters() as Array<{ field: string; operator: string; value: unknown }>,
        limit: limit ? Number(limit) : undefined,
        icp_id: icp?.id,
      });
    },
    onSuccess: (data) => {
      setLastResult(data);
      toast.success(
        `DataMoon import complete — ${data.imported} prospects added.`
      );
      onImported?.(data);
    },
    onError: (e: unknown) => {
      const msg = (e as { message?: string })?.message ?? "Unknown error";
      toast.error(`DataMoon import failed: ${msg}`);
    },
  });

  // ── Extra filter helpers ───────────────────────────────────────────────
  function addExtraFilter() {
    setExtraFilters((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        field: "job_title",
        operator: "contains",
        value: "",
      },
    ]);
    setShowExtras(true);
  }

  function removeExtraFilter(id: string) {
    setExtraFilters((prev) => prev.filter((f) => f.id !== id));
  }

  function updateExtraFilter(id: string, patch: Partial<Omit<FilterRow, "id">>) {
    setExtraFilters((prev) =>
      prev.map((f) => (f.id === id ? { ...f, ...patch } : f))
    );
  }

  // ── Render: loading ─────────────────────────────────────────────────────
  if (statusQ.isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Checking DataMoon access…
        </CardContent>
      </Card>
    );
  }

  // ── Render: not enabled ─────────────────────────────────────────────────
  if (!isEnabled) {
    return (
      <Card>
        <CardContent className="flex items-start gap-3 py-4 text-sm text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            DataMoon prospect sourcing is not enabled for your account. Contact
            your platform administrator to request access.
          </span>
        </CardContent>
      </Card>
    );
  }

  // ── Render: enabled ─────────────────────────────────────────────────────
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Database className="h-4 w-4" />
          Source from DataMoon
        </CardTitle>
        <CardDescription>
          Build an audience with the filters below and import matching prospects
          directly into this ICP.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-5">
        {/* Quota + Limit row */}
        {status && (
          <div className="rounded-md border bg-muted/40 px-4 py-3 space-y-2">
            {/* Quota bar */}
            {status.prospect_quota != null && status.quota_used != null ? (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium text-foreground">
                    Monthly Quota
                  </span>
                  <span className={quotaRemaining != null && quotaRemaining <= 0 ? "text-destructive font-semibold" : "text-muted-foreground"}>
                    {status.quota_used.toLocaleString()} / {status.prospect_quota.toLocaleString()} used
                    {status.quota_reset_at && (
                      <> · resets {new Date(status.quota_reset_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</>
                    )}
                  </span>
                </div>
                <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      quotaRemaining != null && quotaRemaining <= 0
                        ? "bg-destructive"
                        : quotaRemaining != null && quotaRemaining < status.prospect_quota * 0.2
                        ? "bg-amber-500"
                        : "bg-primary"
                    }`}
                    style={{
                      width: `${Math.min(100, (status.quota_used / status.prospect_quota) * 100)}%`,
                    }}
                  />
                </div>
                {quotaRemaining != null && quotaRemaining <= 0 && (
                  <p className="text-xs text-destructive font-medium">Monthly quota exhausted — contact your administrator.</p>
                )}
              </div>
            ) : (
              <Badge variant="outline" className="text-xs">Quota not configured</Badge>
            )}

            {/* Max records */}
            <div className="flex items-center gap-3 pt-1">
              <Label htmlFor="dm-limit" className="shrink-0 text-xs font-medium">
                Max records to fetch
              </Label>
              <Input
                id="dm-limit"
                type="number"
                min={1}
                max={quotaRemaining ?? 5000}
                className="h-7 w-28 text-sm"
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
              />
              {quotaRemaining != null && quotaRemaining > 0 && (
                <span className="text-xs text-muted-foreground">
                  up to {quotaRemaining.toLocaleString()} remaining
                </span>
              )}
            </div>
          </div>
        )}

        <Separator />

        {/* ── Required ICP-Mapped Filters ─────────────────────────────── */}
        <div className="space-y-4">
          <Label className="text-sm font-semibold">
            ICP Audience Filters
          </Label>

          {/* Business Email — always on, not removable */}
          <div className="flex items-center gap-2 rounded-md border border-dashed border-muted-foreground/30 bg-muted/30 px-3 py-2">
            <Badge variant="secondary" className="text-xs">Required</Badge>
            <span className="text-sm text-muted-foreground">
              Business Email — has value
            </span>
          </div>

          {/* ① Industry */}
          <div className="space-y-1.5">
            <Label htmlFor="dm-industry" className="text-sm">
              Industry
            </Label>
            <NativeSelect
              id="dm-industry"
              className="h-9 w-full text-sm"
              value={INDUSTRY_OPTIONS.includes(industry) ? industry : ""}
              onChange={(e) => {
                setIndustry(e.target.value);
                if (e.target.value) setCustomIndustry(""); // clear custom when dropdown chosen
              }}
              disabled={!!customIndustry.trim()}
            >
              <option value="">— Select industry —</option>
              {INDUSTRY_OPTIONS.filter(Boolean).map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </NativeSelect>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground shrink-0">Or type custom:</span>
              <Input
                className="h-8 flex-1 text-sm"
                value={customIndustry}
                onChange={(e) => {
                  setCustomIndustry(e.target.value);
                  if (e.target.value) setIndustry(""); // clear dropdown when typing custom
                }}
                placeholder="e.g. Fintech, PropTech, EdTech…"
              />
            </div>
            {(industry || customIndustry.trim()) && (
              <p className="text-xs text-muted-foreground">
                Active: <span className="font-medium text-foreground">{customIndustry.trim() || industry}</span>
              </p>
            )}
          </div>

          {/* ② State / Region — searchable multi-select dropdown */}
          <div className="space-y-1.5">
            <Label className="text-sm">
              State / Region
              {states.length > 0 && (
                <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                  {states.length} selected
                </span>
              )}
            </Label>
            {/* Selected state pills */}
            {states.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {states.map((s) => (
                  <span
                    key={s}
                    className="inline-flex items-center gap-1 rounded-md border border-primary/30 bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
                  >
                    {s}
                    <button
                      type="button"
                      className="ml-0.5 rounded hover:text-destructive"
                      onClick={() => setStates((prev) => prev.filter((x) => x !== s))}
                      aria-label={`Remove ${s}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
                <button
                  type="button"
                  className="text-xs text-muted-foreground underline-offset-2 hover:underline"
                  onClick={() => setStates([])}
                >
                  Clear all
                </button>
              </div>
            )}
            {/* Search + dropdown */}
            <div className="relative">
              <Input
                className="h-9 text-sm"
                placeholder="Search states…"
                value={stateSearch}
                onChange={(e) => setStateSearch(e.target.value)}
              />
              {stateSearch && (
                <div className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-md border bg-popover shadow-md">
                  {US_STATE_OPTIONS.filter(
                    (o) =>
                      o.label.toLowerCase().includes(stateSearch.toLowerCase()) &&
                      !states.includes(o.value),
                  ).length === 0 ? (
                    <p className="px-3 py-2 text-xs text-muted-foreground">
                      No states match "{stateSearch}"
                    </p>
                  ) : (
                    US_STATE_OPTIONS.filter(
                      (o) =>
                        o.label.toLowerCase().includes(stateSearch.toLowerCase()) &&
                        !states.includes(o.value),
                    ).map((o) => (
                      <button
                        key={o.value}
                        type="button"
                        className="flex w-full items-center px-3 py-1.5 text-left text-sm hover:bg-accent"
                        onClick={() => {
                          setStates((prev) => [...prev, o.value]);
                          setStateSearch("");
                        }}
                      >
                        {o.label}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ③ Company NAICS Code */}
          <div className="space-y-1.5">
            <Label htmlFor="dm-naics" className="text-sm">
              Company NAICS Code
              <span className="ml-1 text-xs text-muted-foreground">(comma-separated)</span>
            </Label>
            <Input
              id="dm-naics"
              className="h-9 text-sm"
              value={naicsCodes}
              onChange={(e) => setNaicsCodes(e.target.value)}
              placeholder="e.g. 5112, 5415, 7372"
            />
          </div>

          {/* ④ Target Seniority — checkbox group */}
          <div className="space-y-1.5">
            <Label className="text-sm">Target Seniority</Label>
            <CheckboxGroup
              options={SENIORITY_OPTIONS}
              selected={seniorities}
              onChange={setSeniorities}
            />
          </div>

          {/* ⑤ Company Size — checkbox group */}
          <div className="space-y-1.5">
            <Label className="text-sm">Company Size (employees)</Label>
            <CheckboxGroup
              options={COMPANY_SIZE_OPTIONS}
              selected={companySizes}
              onChange={setCompanySizes}
            />
          </div>

          {/* ⑥ Company Revenue — checkbox group */}
          <div className="space-y-1.5">
            <Label className="text-sm">Company Revenue</Label>
            <CheckboxGroup
              options={REVENUE_OPTIONS}
              selected={revenue}
              onChange={setRevenue}
            />
          </div>
        </div>

        <Separator />

        {/* ── Additional Filters (collapsible) ────────────────────────── */}
        <div className="space-y-2">
          <button
            type="button"
            className="flex w-full items-center justify-between text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => setShowExtras(!showExtras)}
          >
            <span>Additional Filters ({extraFilters.length})</span>
            {showExtras ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>

          {showExtras && (
            <div className="space-y-2 pl-1">
              {extraFilters.map((row) => (
                <div key={row.id} className="flex items-center gap-2">
                  {/* Field */}
                  <NativeSelect
                    className="h-8 min-w-[170px] text-sm"
                    value={row.field}
                    onChange={(e) =>
                      updateExtraFilter(row.id, { field: e.target.value })
                    }
                  >
                    {EXTRA_FIELD_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </NativeSelect>

                  {/* Operator */}
                  <NativeSelect
                    className="h-8 min-w-[130px] text-sm"
                    value={row.operator}
                    onChange={(e) =>
                      updateExtraFilter(row.id, {
                        operator: e.target.value,
                        value: "",
                      })
                    }
                  >
                    {EXTRA_OPERATOR_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </NativeSelect>

                  {/* Value — hidden for has_value */}
                  {!NO_VALUE_OPERATORS.has(row.operator) && (
                    <Input
                      className="h-8 min-w-[140px] text-sm"
                      placeholder={
                        row.operator === "in" ? "val1, val2, …" : "value"
                      }
                      value={row.value}
                      onChange={(e) =>
                        updateExtraFilter(row.id, { value: e.target.value })
                      }
                    />
                  )}

                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 shrink-0 text-destructive"
                    onClick={() => removeExtraFilter(row.id)}
                    title="Remove filter"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}

              {extraFilters.length === 0 && (
                <p className="text-xs text-muted-foreground py-1">
                  No additional filters added.
                </p>
              )}
            </div>
          )}

          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs"
            onClick={addExtraFilter}
          >
            <Plus className="mr-1 h-3.5 w-3.5" />
            Add filter
          </Button>
        </div>

        <Separator />

        {/* Fetch button */}
        <Button
          className="w-full"
          disabled={
            fetchMut.isPending ||
            (quotaRemaining != null && quotaRemaining <= 0)
          }
          onClick={() => fetchMut.mutate()}
        >
          {fetchMut.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Building audience…
            </>
          ) : (
            <>
              <Database className="mr-2 h-4 w-4" />
              Fetch Prospects
            </>
          )}
        </Button>

        {/* In-progress note */}
        {fetchMut.isPending && (
          <p className="text-center text-xs text-muted-foreground">
            DataMoon is building the audience asynchronously. This may take up
            to 1–2 minutes.
          </p>
        )}

        {/* Last result */}
        {lastResult && !fetchMut.isPending && (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm dark:border-emerald-900 dark:bg-emerald-950/30">
            <div className="flex items-center gap-2 font-medium text-emerald-800 dark:text-emerald-300">
              <CheckCircle2 className="h-4 w-4" />
              Import complete
            </div>
            <ul className="mt-2 space-y-1 text-emerald-700 dark:text-emerald-400">
              <li>
                <strong>{lastResult.imported}</strong> prospects imported
              </li>
              <li>
                <strong>{lastResult.rows_fetched}</strong> records fetched from
                DataMoon (audience #{lastResult.audience_id})
              </li>
              {lastResult.skipped_duplicate > 0 && (
                <li className="text-muted-foreground">
                  {lastResult.skipped_duplicate} skipped — already in your
                  prospect list
                </li>
              )}
              {lastResult.skipped_no_name > 0 && (
                <li className="text-muted-foreground">
                  {lastResult.skipped_no_name} skipped — missing name
                </li>
              )}
            </ul>
          </div>
        )}

        {/* CSV export stored server-side; download UI intentionally hidden */}

        {/* Error state */}
        {fetchMut.isError && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              {(fetchMut.error as { message?: string })?.message ??
                "Import failed. Check your filters and try again."}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default DataMoonSourcePanel;
